import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import { getGiftTable as getGiftTableApi } from "../../services/giftTableApiService";
import { loadTheme } from "../../themes/theme";

interface EventLocation {
  address: string;
  lat: number;
  lng: number;
}

export interface GiftTableProduct {
  productID: string;
  category: string;
  categoryID: string;
  brand: string;
  quantity: number;
  taken: number;
  priority: boolean;
  value: number;
}

export interface GiftTableState {
  ID: string;
  amphitryon: string;
  eventName: string;
  eventType: string;
  eventDate: string;
  eventLocation: EventLocation;
  googleMapsLink: string;
  eventPlace: string;
  messageToGuests: string;
  organizers: { firstName: string; lastName: string }[];
  theme: string;
  profilePicture: string;
  thumbnailSmall: string;
  thumbnailMed: string;
  thumbnailLg: string;
  products: GiftTableProduct[];
  slug: string;
  loading: boolean;
}

export const createBaseGuestsGiftTableState = (): GiftTableState => ({
  ID: "",
  amphitryon: "",
  eventName: "",
  eventType: "",
  eventDate: "",
  eventLocation: {
    address: "",
    lat: 0,
    lng: 0,
  },
  googleMapsLink: "",
  eventPlace: "",
  messageToGuests: "",
  organizers: [],
  theme: "base",
  profilePicture: "",
  thumbnailSmall: "",
  thumbnailMed: "",
  thumbnailLg: "",
  products: [],
  slug: "",
  loading: false,
});

const guestsGiftTableSlice = createSlice({
  name: "guestsGiftTable",
  initialState: createBaseGuestsGiftTableState(),
  reducers: {
    loadGiftTable: (_state, action: PayloadAction<GiftTableState>) => {
      const newState = { ...action.payload, loading: false };
      localStorage.setItem("guestsGiftTableState", JSON.stringify(newState)); // Persist full state
      localStorage.setItem("theme", newState.theme); // Persist theme separately
      // loadTheme(newState.theme); // Apply the theme whenever gift table state is loaded
      return newState;
    },
    clearGiftTable: () => {
      localStorage.removeItem("guestsGiftTableState");
      localStorage.setItem("theme", "base"); // Reset theme to "base" on clear
      loadTheme("base"); // Reset theme visually
      return createBaseGuestsGiftTableState();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
});

export const { loadGiftTable, clearGiftTable, setLoading } =
  guestsGiftTableSlice.actions;

export default guestsGiftTableSlice.reducer;

// Thunk action to fetch and load the gift table
export const fetchAndLoadGiftTable =
  (tableID?: string) => async (dispatch: AppDispatch) => {
    dispatch(setLoading(true)); // Start loading

    try {
      let resolvedTableID = tableID;

      if (!resolvedTableID) {
        const storedState = localStorage.getItem("guestsGiftTableState");
        if (storedState) {
          const parsedState = JSON.parse(storedState) as GiftTableState;
          resolvedTableID = parsedState.ID;
        } else {
          console.error(
            "No table ID provided and no gift table state found in local storage."
          );
          dispatch(setLoading(false));
          return;
        }
      }

      const activeGiftTable = await getGiftTableApi(resolvedTableID);
      const giftTableState: GiftTableState = {
        ID: activeGiftTable.id!,
        amphitryon: activeGiftTable.amphitryon,
        eventName: activeGiftTable.event_name,
        eventType: activeGiftTable.event_type,
        eventDate: activeGiftTable.event_date,
        eventLocation: {
          address: activeGiftTable.event_location.address,
          lat: activeGiftTable.event_location.lat,
          lng: activeGiftTable.event_location.lng,
        },
        googleMapsLink: activeGiftTable.google_maps_link,
        eventPlace: activeGiftTable.event_place,
        messageToGuests: activeGiftTable.message_to_guests,
        organizers:
          activeGiftTable.organizers?.map((organizer) => ({
            firstName: organizer.first_name,
            lastName: organizer.last_name,
          })) || [],
        theme: activeGiftTable.theme,
        profilePicture: activeGiftTable.profile_picture,
        thumbnailSmall: activeGiftTable.thumbnail_small,
        thumbnailMed: activeGiftTable.thumbnail_med,
        thumbnailLg: activeGiftTable.thumbnail_lg,
        products:
          activeGiftTable.products?.map((product) => ({
            productID: product.product_id,
            category: product.category,
            categoryID: product.category_id,
            brand: product.brand,
            quantity: product.quantity,
            taken: product.taken,
            priority: product.priority,
            value: product.value,
          })) || [],
        slug: activeGiftTable.slug || "",
        loading: false,
      };

      // Store the new gift table state in localStorage
      localStorage.setItem(
        "guestsGiftTableState",
        JSON.stringify(giftTableState)
      );
      localStorage.setItem("theme", giftTableState.theme); // Persist theme separately
      // loadTheme(giftTableState.theme); // Load the selected theme

      dispatch(loadGiftTable(giftTableState));
    } catch (error) {
      console.error("Error fetching and loading gift table", error);
      dispatch(setLoading(false)); // Stop loading on error
    }
  };
